import React from "react";
import {
    ContextCell,
    ContextPlane,
    ContextProperty,
    TimeCut
} from "../../model/context";
import {
    normalizeClassName,
    NOTE_WIDTH,
    positionToTick,
    tickToPosition
} from "../../render/renderUtils";
import { useZoomFactor } from "../../stores/ZoomStore";
import { MetronomeInputBox } from "../pickers/ContextMetronome";
import {
    MetronomeMarking,
    NoteDuration,
    TimeSignature,
    Tone
} from "../../model/core";
import TimeSignatureInputBox from "../ContextTimeSignature";
import { RydProject } from "../../model/project";
import { TonalityInputBox } from "../pickers/ContextTonality";
import { Tonality } from "../../model/scales";
import './RydContextLayer.css';
import {
    StateClassWrapper,
    useSetWorkspaceContextPlane,
} from "../../stores/WorkspaceStore";
import { ScalesService } from "../../services/ScalesService";
import { RydFloatingPanel } from "../widgets/RydFloatingPanel";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClose,
    faPlus,
    faRemove
} from "@fortawesome/free-solid-svg-icons";


function makeStyle(
    zoomFactor: number,
    cellStart: number,
    cellLength: number,
    rowIdx: number,
    cell: ContextCell | null
): React.CSSProperties {
    return {
        position: 'relative',
        width: `${tickToPosition(NOTE_WIDTH, zoomFactor, cellLength)}px`,
        backgroundColor: cell ? "#4caf50" : "#ddd",
        filter: cell ? `hue-rotate(calc(${(cellStart * 31 / 29) + (rowIdx * 23 / 3)} * 5deg))` : 'none',
        minHeight: '17px',
    };
}

function EmptyContextCell({
    zoomFactor,
    plane,
    rowIdx,
    start,
    length,
    singleElementRow,
    setWorkspaceContextPlane
}: {
    zoomFactor: number,
    plane: ContextPlane,
    rowIdx: number,
    start: number,
    length: number,
    singleElementRow: boolean,
    setWorkspaceContextPlane: (cp: StateClassWrapper<ContextPlane>) => void
}) {
    return (<div style={makeStyle(zoomFactor, start, length, rowIdx, null)} >
        <div className="context-row-left-spacer" />
        <button onClick={() => {
            const c = new ContextCell(start, length);
            plane.addCellToRow(rowIdx, c);
            setWorkspaceContextPlane({ stateObject: plane });
        }}>
            <FontAwesomeIcon icon={faPlus} />
        </button>
        {singleElementRow && <button onClick={() => {
            plane.removeRow(rowIdx);
            setWorkspaceContextPlane({ stateObject: plane });
        }}>
            <FontAwesomeIcon icon={faRemove} />
        </button>
        }
    </div>);
}

/**
 * Returns a list of TimeCuts representing gaps in the given row of ContextCells up to the specified rowLength.
 * @param cells
 * @param rowLength
 */
function findGapsInRow(
    cells: ContextCell[],
    rowLength: number
): TimeCut[] {
    const gaps: TimeCut[] = [];
    let currentPosition = 0;

    // Sort cells by their start position
    const sortedCells = cells.slice().sort((a, b) => a.start - b.start);
    for (const cell of sortedCells) {
        if (cell.start > currentPosition) {
            // There's a gap before this cell
            gaps.push({ start: currentPosition, end: cell.start - 1 });
        }
        // Move current position to the end of this cell
        currentPosition = Math.max(currentPosition, cell.start + cell.length);
    }

    // Check for a gap at the end of the row
    if (currentPosition < rowLength) {
        gaps.push({ start: currentPosition, end: rowLength - 1 });
    }

    return gaps;
}

function ContextCellPropertyAddButton({
    cell,
    rowIdx,
    plane,
    setWorkspaceContextPlane,
    parent
}: {
    cell: ContextCell,
    rowIdx: number,
    plane: ContextPlane,
    setWorkspaceContextPlane: (cp: StateClassWrapper<ContextPlane>) => void,
    parent: HTMLElement
}) {
    const zoomFactor = useZoomFactor();
    const definedProperties = [
        'metronome',
        'timeSignature',
        'tonality',
    ].map(k => k as keyof ContextProperty).map(k => {
        return { key: k, exists: cell.properties[k] !== undefined }
    });

    const [collpased, setCollapsed] = React.useState(true);
    if (definedProperties.every(p => p.exists)) {
        return null;
    }

    if (collpased) return (<>
        <button onClick={() => setCollapsed(false)}>
            <FontAwesomeIcon icon={faPlus} />
        </button>
        <button onClick={() => {
            plane.getRows()[rowIdx].removeCell(cell);
            setWorkspaceContextPlane({ stateObject: plane });
        }}>
            <FontAwesomeIcon icon={faRemove} />
        </button>
    </>);

    return <RydFloatingPanel referenceElement={parent}
        // FIXME: calculating tick to posirtion here should be unecessary if CSS would be right.
        offsetX={-tickToPosition(NOTE_WIDTH, zoomFactor, cell.start) + 5}>
        <div>
            {!cell.properties.metronome && <button onClick={() => {
                cell.properties.metronome = new MetronomeMarking(120, new NoteDuration(16));
                setWorkspaceContextPlane({ stateObject: plane });
                setCollapsed(true);
            }}>Metronome</button>}
            {!cell.properties.timeSignature && <button onClick={() => {
                cell.properties.timeSignature = new TimeSignature(4, 4);
                setWorkspaceContextPlane({ stateObject: plane });
                setCollapsed(true);
            }}>Time Signature</button>}
            {!cell.properties.tonality && <button onClick={() => {
                cell.properties.tonality = new Tonality(Tone.fromName('C'), ScalesService.get('diatonic')!);
                setWorkspaceContextPlane({ stateObject: plane });
                setCollapsed(true);
            }}>Tonality</button>}
            <button onClick={() => setCollapsed(true)}>
                <FontAwesomeIcon icon={faClose} />
            </button>
        </div>
    </RydFloatingPanel>
}

function ContextPlaneCell({
    cell,
    rowIdx,
    plane,
    setWorkspaceContextPlane,
}: {
    cell: ContextCell,
    rowIdx: number,
    plane: ContextPlane,
    setWorkspaceContextPlane: (cp: StateClassWrapper<ContextPlane>) => void,
}) {

    const cellRef = React.useRef<HTMLDivElement>(null);
    const [resizeX, setResizeX] = React.useState(0);
    const [resizeStartX, setResizeStartX] = React.useState(0);
    const zoomFactor = useZoomFactor();

    const [selectedCell, setSelectedCell] = React.useState<ContextCell | undefined>();

    return (<div ref={cellRef}
        style={makeStyle(zoomFactor, cell.start, cell.length, rowIdx, cell)}
        className={cell === selectedCell ? "context-cell--selected" : undefined}
    >
        <div className={normalizeClassName(["piano-roll--note-duration-handle", "piano-roll--note-duration-handle--start"])}
            draggable={true}
            onMouseOver={() => setSelectedCell(cell)}
            onMouseOut={() => setSelectedCell(undefined)}
            onDragStart={(e: { clientX: number, dataTransfer: any }) => setResizeStartX(e.clientX)}
            onDragEnd={(e) => {
                const deltaTicks = 0 | positionToTick(NOTE_WIDTH, zoomFactor, e.clientX - resizeStartX);
                cell.start += deltaTicks;
                cell.setLength(cell.length - deltaTicks);
                setWorkspaceContextPlane({ stateObject: plane });
            }}
        >&nbsp;</div>

        <div className="context-row-left-spacer" />
        {Object.entries(cell.properties).map(([key, value]) => {
            switch (key) {
                case 'metronome':
                    return <MetronomeInputBox key={key}
                        metronome={value as MetronomeMarking}
                        setMetronomeCB={(m: MetronomeMarking) => {
                            cell.properties.metronome = m;
                            setWorkspaceContextPlane({ stateObject: plane });
                        }}
                    />;
                case 'timeSignature':
                    return <TimeSignatureInputBox key={key}
                        timeSignature={value as TimeSignature}
                        setTimeSignatureCB={(ts: TimeSignature) => {
                            cell.properties.timeSignature = ts;
                            setWorkspaceContextPlane({ stateObject: plane });
                        }}
                    />;
                case 'tonality':
                    return <TonalityInputBox key={key}
                        tonality={value as Tonality}
                        setTonalityCB={(t: Tonality) => {
                            cell.properties.tonality = t;
                            setWorkspaceContextPlane({ stateObject: plane });
                        }}
                        autoClose={false} />;
                default:
                    return <span key={key} style={{ position: 'absolute', top: 0, right: 0, fontSize: 8, color: '#fff' }}>{key}</span>;
            }
        })}
        <ContextCellPropertyAddButton
            cell={cell}
            rowIdx={rowIdx}
            plane={plane}
            setWorkspaceContextPlane={setWorkspaceContextPlane}
            parent={cellRef.current!}
        />
        <div className="piano-roll--note-duration-handle"
            draggable={true}
            onMouseOver={() => setSelectedCell(cell)}
            onMouseOut={() => setSelectedCell(undefined)}
            onDragStart={(e: { clientX: number, dataTransfer: any }) => setResizeX(e.clientX)}
            onDragEnd={(e) => {
                const deltaTicks = 0 | positionToTick(NOTE_WIDTH, zoomFactor, e.clientX - resizeX);
                if (deltaTicks >= cell.length) return;
                cell.setLength(cell.length + deltaTicks);
                setWorkspaceContextPlane({ stateObject: plane });
            }}
        >&nbsp;</div>
    </div>)

}

function RowContents({
    cells,
    project,
    rowIdx,
    zoomFactor,
    plane,
    setWorkspaceContextPlane
}: {
    cells: (ContextCell)[],
    project: RydProject,
    rowIdx: number,
    zoomFactor: number,
    plane: ContextPlane,
    setWorkspaceContextPlane: (cp: StateClassWrapper<ContextPlane>) => void
}) {

    const gaps = findGapsInRow(cells, project.getLength());
    const result = cells.map((cell, i) => {
        return {
            start: cell.start,
            el: <ContextPlaneCell key={i}
                cell={cell}
                rowIdx={rowIdx}
                plane={plane}
                setWorkspaceContextPlane={setWorkspaceContextPlane}
            />
        }
    });

    result.push(...gaps.map((gap, gapIdx) => {
        return {
            start: gap.start, el: <EmptyContextCell
                key={`gap-${gapIdx}`}
                start={gap.start}
                length={gap.end - gap.start + 1}
                rowIdx={rowIdx}
                zoomFactor={zoomFactor}
                plane={plane}
                setWorkspaceContextPlane={setWorkspaceContextPlane}
                singleElementRow={cells.length === 0}
            />
        }
    }));
    return <>
        {result.sort((a, b) => a.start - b.start).map(k => k.el)}
    </>;
}

export function RydContextPlaneGrid({
    project,
    plane
}: {
    project: RydProject,
    plane: ContextPlane
}) {
    const zoomFactor = useZoomFactor();
    const setWorkspaceContextPlane = useSetWorkspaceContextPlane();
    return (<>
        {plane.getRows().map((row, rowIdx) => {
            return <div className="context-layer--row" key={rowIdx}>
                <RowContents
                    cells={plane.getCellsInRow(rowIdx)}
                    project={project}
                    rowIdx={rowIdx}
                    zoomFactor={zoomFactor}
                    plane={plane}
                    setWorkspaceContextPlane={setWorkspaceContextPlane} />
            </div>
        })}
    </>);
}